import {useLoaderData, useRouteLoaderData} from '@remix-run/react'
import {type LoaderFunction, type MetaFunction} from '@remix-run/node'
import type {ComponentType} from 'react'
import {CATEGORIES_FOR_INDEX_BY_DOMAIN_NAME} from 'server/middleware/extend-request.js'
import {getTranslation} from '~/localization/translation'
import type {CategoriesDataProps, RootLoaderResponse} from '~/types/index.js'
import {popularCategories, popularNonUsCategories} from '~/api/home-page-popular-categories'
import {setEnvironmentWith} from '~/utils/async-storage.server.js'
import type {loader as rootLoader} from '~/root.js'
import {getDomainConfig} from '~/domains/index.js'
import {
  fetchHomePageData,
  fetchHomePageFakeData,
  fetchImprovedHomePageData,
  fetchImprovedHomePageFakeData,
} from '~/api/home-page-fetcher.server.js'
import LCategoriesHomepage from './homepages/l-categories/index.js'
import DealsTopRatedHomepage from './homepages/deals-top-rated/index.js'
import ImprovedHomepage from './homepages/improved/index.js'

type HomepageVariant = 'improved' | 'dealsTopRated' | 'lCategories'

type HomepageContext = {
  showImprovedHomepage: boolean
  hasImprovedHomepageData: boolean
  hasDealsTopRatedData: boolean
}

type HomepageRule = {
  variant: HomepageVariant
  when: (context: HomepageContext) => boolean
}

const HOMEPAGE_RULES: HomepageRule[] = [
  {
    variant: 'improved',
    when: ({showImprovedHomepage, hasImprovedHomepageData}) => showImprovedHomepage && hasImprovedHomepageData,
  },
  {
    variant: 'dealsTopRated',
    when: ({showImprovedHomepage, hasDealsTopRatedData}) => !showImprovedHomepage && hasDealsTopRatedData,
  },
  {
    variant: 'lCategories',
    when: () => true,
  },
]

const HOMEPAGE_COMPONENTS: Record<HomepageVariant, ComponentType> = {
  improved: ImprovedHomepage,
  dealsTopRated: DealsTopRatedHomepage,
  lCategories: LCategoriesHomepage,
}

const resolveHomepageVariant = (context: HomepageContext): HomepageVariant => {
  return HOMEPAGE_RULES.find((rule) => rule.when(context))?.variant ?? 'lCategories'
}

export const handle = {
  breadcrumb: 'home',
}

export const meta: MetaFunction<typeof rootLoader> = ({data}: {data: CategoriesDataProps}) => {
  const language = data?.language ?? 'en-US'
  const translations = getTranslation(language)
  const domain = data?.domain ?? 'www.bestreviews.guide'
  const {siteName} = getDomainConfig(domain)
  return [
    {
      title: translations.Home.Page.title,
    },
    {
      name: 'description',
      content: translations.Home.Page.description,
    },
    {
      name: 'viewport',
      content: 'width=device-width,initial-scale=1',
    },
    {
      name: 'charset',
      content: 'utf-8',
    },
    {
      property: 'og:url',
      content: `https://${domain}/categories`,
    },
    {
      property: 'og:locale',
      content: data?.locale?.toUpperCase(),
    },
    {
      property: 'og:site_name',
      content: siteName,
    },
    {
      property: 'og:image:width',
      content: '1024',
    },
    {
      property: 'og:image:height',
      content: '1024',
    },
    {
      property: 'og:title',
      content: translations.Home.Page.ogTitle,
    },
    {
      property: 'og:description',
      content: translations.Home.Page.description,
    },
    {
      property: 'og:type',
      content: 'website',
    },
  ]
}

export const loader: LoaderFunction = async ({context}) => {
  setEnvironmentWith(context)
  const {locale, domain, language, featureFlags} = context
  const {domainName} = getDomainConfig(domain, locale)
  const localeLowerCase = locale.toLowerCase()
  const showImprovedHomepage = Boolean(featureFlags?.showImprovedHomepage)
  const isDomainNonCategorical =
    !CATEGORIES_FOR_INDEX_BY_DOMAIN_NAME[domainName]?.scopes ||
    CATEGORIES_FOR_INDEX_BY_DOMAIN_NAME[domainName]?.scopes?.length === 0
  const imageProxyCredentials = {
    host: process.env.IMAGE_PROXY_HOST!,
    key: process.env.IMAGE_PROXY_KEY!,
  }

  const dealsTopRatedData = !showImprovedHomepage
    ? process.env.NODE_ENV !== 'production'
      ? await fetchHomePageFakeData()
      : await fetchHomePageData({
          domainName,
          locale,
          imageProxyCredentials,
        })
    : null

  const improvedHomepageData = showImprovedHomepage
    ? process.env.NODE_ENV !== 'production'
      ? await fetchImprovedHomePageFakeData()
      : await fetchImprovedHomePageData({
          domainName,
          isDomainNonCategorical,
          locale,
          imageProxyCredentials,
        })
    : null
  const categories =
    localeLowerCase !== 'us' ? (popularNonUsCategories[localeLowerCase] ?? popularCategories) : popularCategories

  return new Response(
    JSON.stringify({
      newHomePageData: dealsTopRatedData?.value,
      improvedHomepageData: improvedHomepageData?.value,
      categories: categories?.categories,
      locale,
      domain,
      language,
      isDomainNonCategorical,
    }),
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )
}

export default function IndexRoute() {
  const {newHomePageData, improvedHomepageData} = useLoaderData() as CategoriesDataProps
  const {featureFlags} = useRouteLoaderData('root') as RootLoaderResponse
  const homepageVariant = resolveHomepageVariant({
    showImprovedHomepage: Boolean(featureFlags.showImprovedHomepage),
    hasImprovedHomepageData: Boolean(improvedHomepageData),
    hasDealsTopRatedData: Boolean(newHomePageData?.homepageData),
  })
  const ActiveHomepage = HOMEPAGE_COMPONENTS[homepageVariant]

  return <ActiveHomepage />
}
